import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Skeleton, Checkbox } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import GroupsIcon from '@mui/icons-material/Groups';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { SectionCard } from './shared';
import { TagBadge } from './TagBadge';
import { AggregatePanel } from './AggregatePanel';
import {
    useGlobalWatchlists,
    useWatchlistMembers,
} from '../api/useGlobalWatchlists';
import type {
    GlobalWatchlistMember,
    GlobalWatchlistSummary,
} from '../api/useGlobalWatchlists';
import {
    cleanAccount,
    formatPnlShort,
    formatPct,
    formatPortfolio,
    formatCount,
} from '../utils/traderFormat';
import { hoverScrollbar } from '../../../styles/hoverScrollbar';
import { useTraderTagData } from '../hooks/useTraderTagData';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string | null): string {
    if (!iso) return '—';
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${dd}.${mm}.${d.getFullYear()} ${hh}:${min}`;
}

function formatDatetime(iso: string): string {
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${dd}.${mm}.${d.getFullYear()} ${hh}:${min}`;
}

// ---------------------------------------------------------------------------
// Sorting
// ---------------------------------------------------------------------------

type SortKey =
    | 'address'
    | 'admitted_at'
    | 'first_seen'
    | 'last_seen'
    | 'portfolio_value'
    | 'total_pnl'
    | 'win_rate'
    | 'avg_edge'
    | 'num_trades'
    | 'num_markets';

type SortDir = 'asc' | 'desc';

function getSortValue(
    member: GlobalWatchlistMember,
    key: SortKey
): number | string {
    switch (key) {
        case 'address':
            return cleanAccount(member.address).toLowerCase();
        case 'admitted_at':
            return new Date(member.admitted_at).getTime();
        case 'first_seen':
            return new Date(member.first_seen).getTime();
        case 'last_seen':
            return new Date(member.last_seen).getTime();
        case 'portfolio_value':
            return member.portfolio_value ?? -Infinity;
        case 'total_pnl':
            return member.total_pnl ?? -Infinity;
        case 'win_rate':
            return member.win_rate ?? -Infinity;
        case 'avg_edge':
            return member.avg_edge ?? -Infinity;
        case 'num_trades':
            return member.num_trades ?? -Infinity;
        case 'num_markets':
            return member.num_markets ?? -Infinity;
    }
}

function sortMembers(
    members: GlobalWatchlistMember[],
    key: SortKey | null,
    dir: SortDir
): GlobalWatchlistMember[] {
    if (!key) return members;
    return [...members].sort((a, b) => {
        const av = getSortValue(a, key);
        const bv = getSortValue(b, key);
        const cmp =
            typeof av === 'string' && typeof bv === 'string'
                ? av.localeCompare(bv)
                : (av as number) - (bv as number);
        return dir === 'asc' ? cmp : -cmp;
    });
}

const SORT_STORAGE_PREFIX = 'watchlist-sort:';

function loadStoredSort(groupName: string): {
    key: SortKey | null;
    dir: SortDir;
} {
    try {
        const raw = sessionStorage.getItem(SORT_STORAGE_PREFIX + groupName);
        if (!raw) return { key: null, dir: 'desc' };
        const parsed = JSON.parse(raw) as {
            key?: SortKey | null;
            dir?: SortDir;
        };
        return {
            key: parsed.key ?? null,
            dir: parsed.dir === 'asc' ? 'asc' : 'desc',
        };
    } catch {
        return { key: null, dir: 'desc' };
    }
}

function saveStoredSort(
    groupName: string,
    key: SortKey | null,
    dir: SortDir
): void {
    try {
        sessionStorage.setItem(
            SORT_STORAGE_PREFIX + groupName,
            JSON.stringify({ key, dir })
        );
    } catch {
        return;
    }
}

// ---------------------------------------------------------------------------
// Watchlist overview card
// ---------------------------------------------------------------------------

function WatchlistCard({
    watchlist,
    selected,
    onClick,
}: {
    watchlist: GlobalWatchlistSummary;
    selected: boolean;
    onClick: () => void;
}) {
    const theme = useTheme();

    return (
        <SectionCard
            sx={{
                mb: 1,
                borderLeft: `3px solid ${selected ? theme.palette.primary.main : 'transparent'}`,
                transition: 'border-color 0.15s',
            }}
        >
            <Box
                onClick={onClick}
                sx={{
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    cursor: 'pointer',
                    bgcolor: selected
                        ? alpha(theme.palette.primary.main, 0.04)
                        : undefined,
                    transition: 'background 0.15s',
                    '&:hover': {
                        bgcolor: alpha(theme.palette.action.hover, 0.06),
                    },
                }}
            >
                <GroupsIcon
                    sx={{
                        fontSize: 20,
                        color: 'text.secondary',
                        flexShrink: 0,
                    }}
                />

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
                        {watchlist.group_name}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: '11px',
                            color: 'text.secondary',
                            mt: '2px',
                        }}
                    >
                        {watchlist.oldest_admission
                            ? `Since ${formatDate(watchlist.oldest_admission)}`
                            : 'No admissions yet'}
                    </Typography>
                </Box>

                <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                    <Typography
                        sx={{
                            fontSize: '20px',
                            fontWeight: 700,
                            lineHeight: 1,
                        }}
                    >
                        {watchlist.member_count}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: '10px',
                            color: 'text.secondary',
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                        }}
                    >
                        members
                    </Typography>
                </Box>

                <ChevronRightIcon
                    sx={{
                        fontSize: 18,
                        color: selected ? 'primary.main' : 'text.disabled',
                        flexShrink: 0,
                        transition: 'color 0.15s',
                    }}
                />
            </Box>
        </SectionCard>
    );
}

// ---------------------------------------------------------------------------
// Single member row
// ---------------------------------------------------------------------------

function MemberRow({
    member,
    tags,
    dataIssues,
    selected,
    onToggle,
    onNavigate,
}: {
    member: GlobalWatchlistMember;
    tags: string[];
    dataIssues?: string[];
    selected: boolean;
    onToggle: () => void;
    onNavigate: () => void;
}) {
    const theme = useTheme();

    const pnlColor =
        member.total_pnl == null
            ? undefined
            : member.total_pnl >= 0
              ? theme.palette.success.main
              : theme.palette.error.main;

    return (
        <tr style={{ cursor: 'default' }}>
            <td style={{ width: 32, textAlign: 'center' }}>
                <Checkbox
                    size="small"
                    checked={selected}
                    onChange={onToggle}
                    onClick={e => e.stopPropagation()}
                    sx={{ p: 0 }}
                />
            </td>
            <td
                onClick={onNavigate}
                style={{
                    fontSize: '11px',
                    fontFamily: 'monospace',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                }}
            >
                {member.address}
            </td>
            <td style={{ cursor: 'default' }}>
                {tags.length > 0 ? (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                        }}
                    >
                        {tags.map(tag => (
                            <TagBadge
                                key={tag}
                                tag={tag}
                                dataIssues={dataIssues}
                            />
                        ))}
                    </Box>
                ) : (
                    <Typography
                        sx={{ fontSize: '10px', color: 'text.disabled' }}
                    >
                        —
                    </Typography>
                )}
            </td>
            <td
                style={{
                    fontSize: '11px',
                    fontFamily: 'monospace',
                    textAlign: 'right',
                }}
            >
                {formatPortfolio(member.portfolio_value)}
            </td>
            <td
                style={{
                    fontSize: '11px',
                    fontFamily: 'monospace',
                    textAlign: 'right',
                    color: pnlColor,
                }}
            >
                {formatPnlShort(member.total_pnl)}
            </td>
            <td
                style={{
                    fontSize: '11px',
                    fontFamily: 'monospace',
                    textAlign: 'right',
                }}
            >
                {formatPct(member.win_rate)}
            </td>
            <td
                style={{
                    fontSize: '11px',
                    fontFamily: 'monospace',
                    textAlign: 'right',
                }}
            >
                {formatPct(member.avg_edge)}
            </td>
            <td
                style={{
                    fontSize: '11px',
                    fontFamily: 'monospace',
                    textAlign: 'right',
                }}
            >
                {formatCount(member.num_trades)}
            </td>
            <td
                style={{
                    fontSize: '11px',
                    fontFamily: 'monospace',
                    textAlign: 'right',
                }}
            >
                {formatCount(member.num_markets)}
            </td>
            <td
                style={{
                    fontSize: '10px',
                    fontFamily: 'monospace',
                    color: theme.palette.text.secondary,
                    whiteSpace: 'nowrap',
                    textAlign: 'right',
                }}
            >
                {formatDatetime(member.first_seen)}
            </td>
            <td
                style={{
                    fontSize: '10px',
                    fontFamily: 'monospace',
                    color: theme.palette.text.secondary,
                    whiteSpace: 'nowrap',
                    textAlign: 'right',
                }}
            >
                {formatDatetime(member.last_seen)}
            </td>
            <td
                style={{
                    fontSize: '10px',
                    fontFamily: 'monospace',
                    color: theme.palette.text.secondary,
                    whiteSpace: 'nowrap',
                    textAlign: 'right',
                }}
            >
                {formatDatetime(member.admitted_at)}
            </td>
            <td style={{ width: 28, textAlign: 'center' }}>
                <Box
                    component="span"
                    onClick={onNavigate}
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        opacity: 0.35,
                        cursor: 'pointer',
                        '&:hover': { opacity: 1 },
                    }}
                >
                    <OpenInNewIcon
                        sx={{ fontSize: 12, color: 'text.secondary' }}
                    />
                </Box>
            </td>
        </tr>
    );
}

// ---------------------------------------------------------------------------
// Sortable column header
// ---------------------------------------------------------------------------

function SortableTh({
    label,
    sortKey,
    currentKey,
    currentDir,
    onSort,
    style,
}: {
    label: string;
    sortKey: SortKey;
    currentKey: SortKey | null;
    currentDir: SortDir;
    onSort: (key: SortKey) => void;
    style?: React.CSSProperties;
}) {
    const active = currentKey === sortKey;

    return (
        <th
            onClick={() => onSort(sortKey)}
            style={{
                ...style,
                cursor: 'pointer',
                userSelect: 'none',
                whiteSpace: 'nowrap',
            }}
        >
            <Box
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '3px',
                    opacity: active ? 1 : undefined,
                }}
            >
                {label}
                {active ? (
                    currentDir === 'asc' ? (
                        <ArrowUpwardIcon sx={{ fontSize: 10 }} />
                    ) : (
                        <ArrowDownwardIcon sx={{ fontSize: 10 }} />
                    )
                ) : (
                    // Reserve space so columns don't shift on activation
                    <Box sx={{ width: 10, display: 'inline-block' }} />
                )}
            </Box>
        </th>
    );
}

// ---------------------------------------------------------------------------
// Members panel
// ---------------------------------------------------------------------------

const MAX_SELECTED = 25;

function MembersPanel({ groupName }: { groupName: string }) {
    const theme = useTheme();
    const navigate = useNavigate();
    const { data, loading, error } = useWatchlistMembers(groupName);

    const [sortKey, setSortKey] = useState<SortKey | null>(
        () => loadStoredSort(groupName).key
    );
    const [sortDir, setSortDir] = useState<SortDir>(
        () => loadStoredSort(groupName).dir
    );
    const [selected, setSelected] = useState<Set<string>>(new Set());

    // Load saved sort + reset selection when switching watchlists
    useEffect(() => {
        const stored = loadStoredSort(groupName);
        setSortKey(stored.key);
        setSortDir(stored.dir);
        setSelected(new Set());
    }, [groupName]);

    const addresses = useMemo(
        () => (data?.members ?? []).map(m => m.address),
        [data]
    );
    const { tags: tagsMap, dataIssues: issuesMap } = useTraderTagData(
        addresses,
        cleanAccount
    );

    // Clicking the active column cycles asc → desc → reset; a new column starts desc
    function handleSort(key: SortKey) {
        let nextKey: SortKey | null;
        let nextDir: SortDir;
        if (sortKey === key) {
            if (sortDir === 'desc') {
                nextKey = key;
                nextDir = 'asc';
            } else {
                nextKey = null;
                nextDir = 'desc';
            }
        } else {
            nextKey = key;
            nextDir = 'desc';
        }
        setSortKey(nextKey);
        setSortDir(nextDir);
        saveStoredSort(groupName, nextKey, nextDir);
    }

    function toggle(address: string) {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(address)) {
                next.delete(address);
            } else if (next.size < MAX_SELECTED) {
                next.add(address);
            }
            return next;
        });
    }

    const sortedMembers = useMemo(
        () => sortMembers(data?.members ?? [], sortKey, sortDir),
        [data, sortKey, sortDir]
    );

    const selectedMembers = useMemo(
        () => (data?.members ?? []).filter(m => selected.has(m.address)),
        [data, selected]
    );

    const selectableCount = Math.min(sortedMembers.length, MAX_SELECTED);
    const allSelected = selected.size > 0 && selected.size >= selectableCount;
    const someSelected = selected.size > 0 && selected.size < selectableCount;

    // Header checkbox: clear if anything is selected, else fill up to the cap
    function toggleAll() {
        setSelected(prev => {
            if (prev.size > 0) return new Set();
            return new Set(
                sortedMembers.slice(0, MAX_SELECTED).map(m => m.address)
            );
        });
    }

    const thProps = {
        currentKey: sortKey,
        currentDir: sortDir,
        onSort: handleSort,
    };

    if (loading) {
        return (
            <Box sx={{ p: 2 }}>
                {[...Array(6)].map((_, i) => (
                    <Skeleton
                        key={i}
                        variant="rectangular"
                        height={28}
                        sx={{ mb: 0.5, borderRadius: 0.5 }}
                    />
                ))}
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography sx={{ fontSize: '12px', color: 'text.disabled' }}>
                    Failed to load members
                </Typography>
            </Box>
        );
    }

    if (!data || data.members.length === 0) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography sx={{ fontSize: '12px', color: 'text.disabled' }}>
                    No members
                </Typography>
            </Box>
        );
    }

    return (
        <>
            <Box
                component="table"
                sx={{
                    width: '100%',
                    tableLayout: 'fixed',
                    borderCollapse: 'collapse',
                    '& th': {
                        textAlign: 'left',
                        fontWeight: 600,
                        fontSize: '9.5px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        color: 'text.secondary',
                        py: 0.5,
                        px: 0.75,
                        borderBottom: 1,
                        borderColor: 'divider',
                    },
                    '& td': {
                        py: 0.5,
                        px: 0.75,
                        borderBottom: 1,
                        borderColor: alpha(theme.palette.divider, 0.25),
                        verticalAlign: 'middle',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    },
                    '& tbody tr': {
                        transition: 'background 0.1s',
                        '&:hover td': { bgcolor: 'action.hover' },
                    },
                }}
            >
                <thead>
                    <tr>
                        <th style={{ width: 32, textAlign: 'center' }}>
                            <Checkbox
                                size="small"
                                checked={allSelected}
                                indeterminate={someSelected}
                                onChange={toggleAll}
                                sx={{ p: 0 }}
                            />
                        </th>
                        <SortableTh
                            label="Address"
                            sortKey="address"
                            style={{ width: 300 }}
                            {...thProps}
                        />
                        <th>Tags</th>
                        <SortableTh
                            label="Portfolio"
                            sortKey="portfolio_value"
                            style={{ width: 90, textAlign: 'right' }}
                            {...thProps}
                        />
                        <SortableTh
                            label="PnL"
                            sortKey="total_pnl"
                            style={{ width: 80, textAlign: 'right' }}
                            {...thProps}
                        />
                        <SortableTh
                            label="Win Rate"
                            sortKey="win_rate"
                            style={{ width: 80, textAlign: 'right' }}
                            {...thProps}
                        />
                        <SortableTh
                            label="Avg Edge"
                            sortKey="avg_edge"
                            style={{ width: 80, textAlign: 'right' }}
                            {...thProps}
                        />
                        <SortableTh
                            label="Trades"
                            sortKey="num_trades"
                            style={{ width: 70, textAlign: 'right' }}
                            {...thProps}
                        />
                        <SortableTh
                            label="Markets"
                            sortKey="num_markets"
                            style={{ width: 70, textAlign: 'right' }}
                            {...thProps}
                        />
                        <SortableTh
                            label="Active Since"
                            sortKey="first_seen"
                            style={{ width: 130, textAlign: 'right' }}
                            {...thProps}
                        />
                        <SortableTh
                            label="Last Trade"
                            sortKey="last_seen"
                            style={{ width: 130, textAlign: 'right' }}
                            {...thProps}
                        />
                        <SortableTh
                            label="Admitted"
                            sortKey="admitted_at"
                            style={{ width: 140, textAlign: 'right' }}
                            {...thProps}
                        />
                        <th style={{ width: 28 }} />
                    </tr>
                </thead>
                <tbody>
                    {sortedMembers.map(member => (
                        <MemberRow
                            key={member.address}
                            member={member}
                            tags={
                                tagsMap.get(cleanAccount(member.address)) ?? []
                            }
                            dataIssues={issuesMap.get(
                                cleanAccount(member.address)
                            )}
                            selected={selected.has(member.address)}
                            onToggle={() => toggle(member.address)}
                            onNavigate={() =>
                                navigate(
                                    `/markets/trader/${cleanAccount(member.address)}`
                                )
                            }
                        />
                    ))}
                </tbody>
            </Box>

            {selected.size >= 2 && (
                <AggregatePanel
                    members={selectedMembers}
                    onClear={() => setSelected(new Set())}
                />
            )}
        </>
    );
}

// ---------------------------------------------------------------------------
// WatchlistView
// ---------------------------------------------------------------------------

export function WatchlistView() {
    const theme = useTheme();
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedGroup = searchParams.get('group');

    const { data, loading, error } = useGlobalWatchlists();

    function selectGroup(name: string) {
        setSearchParams(name === selectedGroup ? {} : { group: name });
    }

    function clearGroup() {
        setSearchParams({});
    }

    const selectedWatchlist = useMemo(
        () =>
            data?.watchlists.find(w => w.group_name === selectedGroup) ?? null,
        [data, selectedGroup]
    );

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minHeight: 0,
            }}
        >
            {/* Page header */}
            <Box sx={{ px: 2.5, pt: 2.5, pb: 1.5, flexShrink: 0 }}>
                <Typography sx={{ fontSize: '18px', fontWeight: 700 }}>
                    Watchlists
                </Typography>
                <Typography
                    sx={{ fontSize: '12px', color: 'text.secondary', mt: 0.25 }}
                >
                    Global trader groups
                </Typography>
            </Box>

            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    gap: 0,
                    overflow: 'hidden',
                }}
            >
                {/* Left panel — watchlist overview */}
                <Box
                    sx={{
                        width: selectedGroup ? 360 : '100%',
                        maxWidth: selectedGroup ? 360 : undefined,
                        flexShrink: 0,
                        overflowY: 'auto',
                        px: 2,
                        pb: 2,
                        borderRight: selectedGroup ? 1 : 0,
                        borderColor: 'divider',
                        transition: 'width 0.2s',
                        ...hoverScrollbar,
                    }}
                >
                    {loading && (
                        <>
                            {[...Array(4)].map((_, i) => (
                                <Skeleton
                                    key={i}
                                    variant="rectangular"
                                    height={68}
                                    sx={{ mb: 1, borderRadius: 1 }}
                                />
                            ))}
                        </>
                    )}

                    {error && (
                        <Box sx={{ p: 2, textAlign: 'center' }}>
                            <Typography
                                sx={{
                                    fontSize: '12px',
                                    color: 'text.disabled',
                                }}
                            >
                                Failed to load watchlists
                            </Typography>
                        </Box>
                    )}

                    {data?.watchlists.map(w => (
                        <WatchlistCard
                            key={w.group_name}
                            watchlist={w}
                            selected={w.group_name === selectedGroup}
                            onClick={() => selectGroup(w.group_name)}
                        />
                    ))}
                </Box>

                {/* Right panel — members */}
                {selectedGroup && (
                    <Box
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                        }}
                    >
                        {/* Members panel header */}
                        <Box
                            sx={{
                                px: 2,
                                py: 1.25,
                                borderBottom: 1,
                                borderColor: 'divider',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                flexShrink: 0,
                            }}
                        >
                            <Box
                                component="span"
                                onClick={clearGroup}
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    color: 'text.secondary',
                                    opacity: 0.6,
                                    '&:hover': { opacity: 1 },
                                    mr: 0.5,
                                }}
                            >
                                <ArrowBackIcon sx={{ fontSize: 16 }} />
                            </Box>

                            <Typography
                                sx={{ fontSize: '13px', fontWeight: 600 }}
                            >
                                {selectedGroup}
                            </Typography>

                            {selectedWatchlist && (
                                <Typography
                                    sx={{
                                        fontSize: '11px',
                                        color: 'text.secondary',
                                        ml: 0.5,
                                    }}
                                >
                                    {selectedWatchlist.member_count} members
                                </Typography>
                            )}

                            {selectedWatchlist?.newest_admission && (
                                <Typography
                                    sx={{
                                        fontSize: '10px',
                                        color: 'text.disabled',
                                        ml: 'auto',
                                    }}
                                >
                                    Last admitted{' '}
                                    {formatDate(
                                        selectedWatchlist.newest_admission
                                    )}
                                </Typography>
                            )}
                        </Box>

                        {/* Scrollable members table */}
                        <Box
                            sx={{
                                flex: 1,
                                minHeight: 0,
                                overflowY: 'auto',
                                ...hoverScrollbar,
                            }}
                        >
                            <MembersPanel groupName={selectedGroup} />
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
}
